import { useState } from 'react';
import { ClipboardList, AlertTriangle, RefreshCw, Loader2, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AttributionSource {
  sessionSource?: string | null;
  medium?: string | null;
  mediumId?: string | null;
}

export interface SalesBackground {
  status: 'success' | 'no_match';
  pulled_at?: string;
  pulled_by_contact_id?: string;
  contact_summary?: { name: string | null; email: string | null };
  snapshot?: {
    enquiry_notes: string | null;
    source: string | null;
    attribution_source: AttributionSource | null;
    date_added: string | null;
  };
  prefill_values?: Record<string, string | null>;
  lookup?: { type: string; value: string };
}

interface Props {
  matterId: string;
  primaryContactEmail: string | null;
  ghlContactId: string | null;
  background: SalesBackground | null;
  /** Called after a successful re-pull so the parent reloads the matter row. */
  onRepulled: () => void;
  /** Whether the current user can re-pull (admin only). */
  canRepull: boolean;
}

const ENQUIRY_NOTES_PREVIEW_LEN = 400;

/**
 * Read-only snapshot of the GoHighLevel sales context taken at matter
 * creation. Lives at the top of the Matter Overview main column. Hidden when
 * background is null (pull hasn't been attempted yet).
 */
export default function SalesBackgroundPanel({
  matterId,
  primaryContactEmail,
  ghlContactId,
  background,
  onRepulled,
  canRepull,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [repulling, setRepulling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRepull = async () => {
    setRepulling(true);
    setError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData.session?.access_token;
      if (!jwt) throw new Error('Not signed in');
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ghl-pull-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify(
          ghlContactId
            ? { matter_id: matterId, ghl_contact_id: ghlContactId }
            : { matter_id: matterId, email: primaryContactEmail },
        ),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? `Pull failed (${res.status})`);
      onRepulled();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Re-pull failed');
    } finally {
      setRepulling(false);
    }
  };

  if (!background) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 flex items-start gap-3">
        <ClipboardList size={18} className="text-slate-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-600">No GHL data pulled yet.</div>
          <div className="text-xs text-slate-500 mt-0.5">
            This matter was created before the GHL sync was set up.
            {canRepull ? ' Pull now to import sales background and prefill intake fields.' : ''}
          </div>
          {canRepull && (
            <button
              type="button"
              onClick={handleRepull}
              disabled={repulling}
              className="mt-2 text-xs text-[#1A8DC7] font-semibold inline-flex items-center gap-1.5 hover:underline disabled:opacity-50"
            >
              {repulling ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <RefreshCw size={11} />
              )}
              Pull from GHL
            </button>
          )}
          {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
        </div>
      </div>
    );
  }

  if (background.status === 'no_match') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 flex items-start gap-3">
        <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-900">No GHL contact found.</div>
          <div className="text-xs text-amber-800 mt-0.5">
            We couldn't match this client in GoHighLevel
            {background.lookup ? ` (looked up by ${background.lookup.type}: ${background.lookup.value})` : ''}.
            The intake form fields are blank, no sales background to show. Re-run the pull if the
            client was added to GHL after this matter was created.
          </div>
          {canRepull && (
            <button
              type="button"
              onClick={handleRepull}
              disabled={repulling}
              className="mt-2 text-xs text-amber-900 font-semibold inline-flex items-center gap-1.5 hover:underline disabled:opacity-50"
            >
              {repulling ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <RefreshCw size={11} />
              )}
              Re-pull from GHL
            </button>
          )}
          {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
        </div>
      </div>
    );
  }

  const enquiry = background.snapshot?.enquiry_notes ?? '';
  const longEnquiry = enquiry.length > ENQUIRY_NOTES_PREVIEW_LEN;
  const visibleEnquiry = expanded || !longEnquiry ? enquiry : enquiry.slice(0, ENQUIRY_NOTES_PREVIEW_LEN) + '…';

  return (
    <div className="rounded-lg border border-[#2CABEA]/30 bg-[#EBF7FD]/40 overflow-hidden">
      <div className="px-4 pt-3 pb-2 border-b border-[#2CABEA]/20 flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wide text-[#1A8DC7] font-semibold inline-flex items-center gap-2">
          <ClipboardList size={13} /> Background from sales
        </h2>
        {canRepull && (
          <button
            type="button"
            onClick={handleRepull}
            disabled={repulling}
            title="Re-pull from GHL (fills blanks, doesn't overwrite edits)"
            className="text-xs text-slate-500 hover:text-[#1A8DC7] inline-flex items-center gap-1 disabled:opacity-50"
          >
            {repulling ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <RefreshCw size={11} />
            )}
            Re-pull
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Metadata strip */}
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
          {background.snapshot?.source && (
            <span className="inline-flex items-center gap-1">
              {/^https?:\/\//.test(background.snapshot.source) || background.snapshot.source.includes('.com') ? (
                <ExternalLink size={11} className="text-slate-400" />
              ) : null}
              <span>{cleanSource(background.snapshot.source)}</span>
            </span>
          )}
          {background.snapshot?.attribution_source && (
            <span>
              {[
                background.snapshot.attribution_source.medium,
                background.snapshot.attribution_source.sessionSource,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
          {background.snapshot?.date_added && (
            <span>First inquiry {formatDate(background.snapshot.date_added)}</span>
          )}
        </div>

        {/* Verbatim enquiry notes */}
        {enquiry ? (
          <div className="text-sm text-[#0B2341] whitespace-pre-line">
            {visibleEnquiry}
            {longEnquiry && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="ml-1 text-xs text-[#1A8DC7] hover:underline font-medium"
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-500 italic">
            No enquiry notes captured at lead time.
          </div>
        )}

        {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
      </div>
    </div>
  );
}

function cleanSource(s: string): string {
  // GHL source values are often `Unbounce-https://www.partnersvisaaustralia.com.au/` —
  // friendly format strips the prefix when a URL is present.
  const m = s.match(/^([^-]+)-(https?:\/\/.+)$/);
  if (m) return `${m[1]} · ${m[2].replace(/\/$/, '')}`;
  return s;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}
